import { useState, useEffect } from 'react'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import Input from '../ui/Input'
import LoadingState from '../ui/LoadingState'
import EmptyState from '../ui/EmptyState'
import { reporteService } from '../../services/reporteService'
import Chart from 'react-apexcharts'
import {
  createArtemisAxisOptions,
  createArtemisBaseOptions,
  getArtemisChartTokens,
  getArtemisPalette,
} from '../../utils/artemisApexTheme'
import { Users, Download, RefreshCw, AlertCircle, TrendingUp, UserCheck, UserX } from 'lucide-react'

export default function ReporteUsuarios({ idClinica = null }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [fechaInicio, setFechaInicio] = useState(
    new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]
  )
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => {
    // Solo hacer fetch si idClinica está definido (incluyendo 0) o si es null explícitamente después de que el usuario se haya cargado
    if (idClinica !== undefined) {
      fetchData()
    }
  }, [fechaInicio, fechaFin, idClinica])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }
      // IMPORTANTE: Solo agregar id_clinica si es un número válido mayor que 0 (no null, no undefined, no 0)
      // null y undefined significan "dejar que el backend lo resuelva automáticamente"
      if (idClinica != null && idClinica !== undefined && !isNaN(Number(idClinica)) && Number(idClinica) > 0) {
        params.id_clinica = Number(idClinica)
      }
      
      // Debug: verificar qué parámetros se están enviando
      console.log('🔍 ReporteUsuarios - Parámetros enviados:', { idClinica, params })
      
      const response = await reporteService.getReporteUsuarios(params)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.error || 'Error al cargar reporte')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('❌ Error en ReporteUsuarios:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando reporte de usuarios..." size="lg" />
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button onClick={fetchData} variant="secondary" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return <EmptyState icon={Users} title="No hay datos disponibles" />
  }

  const palette = getArtemisPalette()
  const axisOptions = createArtemisAxisOptions()
  const tokens = getArtemisChartTokens()

  // Configuración gráfico por tipo
  const donutBaseOptions = createArtemisBaseOptions({
    type: 'donut',
    colors: palette.slice(0, 5),
  })
  const chartTipoOptions = {
    ...donutBaseOptions,
    labels: data.usuariosPorTipo.map(item => item.tipo),
    stroke: {
      width: 2,
      colors: [tokens.isDark ? '#1a2535' : '#ffffff'],
    },
    plotOptions: {
      pie: {
        donut: {
          size: '66%',
        },
      },
    },
    legend: {
      ...donutBaseOptions.legend,
      position: 'bottom',
    },
    tooltip: {
      ...donutBaseOptions.tooltip,
      y: {
        formatter: (val, { seriesIndex }) => {
          return `${val} usuarios (${data.usuariosPorTipo[seriesIndex]?.porcentaje || 0}%)`
        }
      }
    },
  }

  const chartTipoSeries = data.usuariosPorTipo.map(item => item.cantidad)

  // Configuración gráfico crecimiento mensual
  const lineBaseOptions = createArtemisBaseOptions({
    type: 'line',
    height: 350,
    colors: [palette[1]],
  })
  const chartCrecimientoOptions = {
    ...lineBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.crecimientoMensual.map(item => item.mesCorto),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 4,
      hover: { size: 6 },
    },
    tooltip: {
      ...lineBaseOptions.tooltip,
    },
    grid: {
      ...lineBaseOptions.grid,
      padding: { top: 8, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartCrecimientoSeries = [{
    name: 'Usuarios Registrados',
    data: data.crecimientoMensual.map(item => item.cantidad)
  }]

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros de Fecha</h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
            <Input
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
            <div className="flex items-end">
              <Button onClick={fetchData} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Usuarios</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{data.totalUsuarios}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cuentas activas</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">{data.usuariosActivos}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <UserCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cuentas desactivadas</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">{data.usuariosInactivos}</p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <UserX className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Registrados en Período</p>
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mt-2">{data.usuariosEnPeriodo}</p>
              </div>
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <TrendingUp className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Distribución por Tipo</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartTipoOptions}
              series={chartTipoSeries}
              type="donut"
              height={350}
            />
            <div className="mt-4 space-y-2">
              {data.usuariosPorTipo.map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{item.tipo}</span>
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-900 dark:text-white font-medium">{item.cantidad}</span>
                    <span className="text-gray-500 dark:text-gray-500">({item.porcentaje}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Crecimiento Mensual</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartCrecimientoOptions}
              series={chartCrecimientoSeries}
              type="line"
              height={350}
            />
          </CardContent>
        </Card>
      </div>

      {/* Tabla de detalles */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Detalle por Tipo</h3>
            <Button variant="secondary" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo de Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cantidad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Porcentaje</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {data.usuariosPorTipo.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{item.tipo}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.cantidad}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

